import { IFile } from '@commenda/types/src/file/types';
import {
  Country,
  Currency,
  MarketplaceProductBillingInterval,
  MarketplaceProductPricingVariant,
  MarketplaceServiceProviderType,
  MarketplaceServiceStatus,
  MarketplaceServiceType,
} from '@prisma/client';

export enum GenericStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Submitted = 'Submitted',
  Completed = 'Completed',
  Filed = 'Filed',
  ActionRequired = 'ActionRequired',
  Archived = 'Archived',
  Ready = 'Ready',
  CompletedByAccountant = 'CompletedByAccountant',
  StartedBooking = 'StartedBooking',
  CallBooked = 'CallBooked',
  ServicePaid = 'ServicePaid',
}

export function getPrintableGenericStatus(status: GenericStatus): string {
  const record: Record<GenericStatus, string> = {
    [GenericStatus.Completed]: 'Completed',
    [GenericStatus.InProgress]: 'In progress',
    [GenericStatus.NotStarted]: 'Not started',
    [GenericStatus.Submitted]: 'Submitted',
    [GenericStatus.ActionRequired]: 'Action Required',
    [GenericStatus.Filed]: 'Filed',
    [GenericStatus.Archived]: 'Archived',
    [GenericStatus.Ready]: 'Ready',
    [GenericStatus.CompletedByAccountant]: 'Completed',
    [GenericStatus.StartedBooking]: 'Started Booking',
    [GenericStatus.CallBooked]: 'Call Booked',
    [GenericStatus.ServicePaid]: 'Paid',
  };

  return record[status];
}

export type TStatusUpdate = {
  statusUpdateTime: Date;
  status?: GenericStatus;
  comment?: string;
  updatingUser: {
    name: string;
    email: string;
    id: string;
  } | null;
};

/* --------------------- NEW MARKETPLACE SERVICE TYPES ---------------------*/

interface IMarketplaceProductVendorPricingVariant {
  id: string;
  discountCode: string | null;
  productByVendorId: string;
  productPricingVariantId: string;
  sla: {
    maxDay: string;
    minDay: string;
  };
  totalPrice: number;
  vendorSpecificPriceUSCents: number;
  priceDisplaySuffix: string;
}

interface IMarketplaceProductPricingVariant {
  id: string;
  productId: string;
  country: Country;
  jurisdiction: string;
  internalMargin: number;
  currency: Currency;
}

export type TMarketplaceProduct = {
  id: string;
  title: string;
  description: string;
  isActive: boolean;
  category: MarketplaceServiceType;
  billingInterval: MarketplaceProductBillingInterval;
  providedByTypes: MarketplaceServiceProviderType[];
  pricingVariants: IMarketplaceProductPricingVariant[];
  isExperimental: boolean;
  vendorOfferings: {
    id: string;
    caFirm: {
      id: string;
      name: string;
      description: string;
    };
    pricingVariants: IMarketplaceProductVendorPricingVariant[];
    productId: string;
    redirectionLinkForPurchase: string;
    description: string;
    meetingLink: string;
    steps: string[];
    firmServiceDetails: string[];
  }[];
};

export type TMarketplaceVendorService = {
  id: string;
  steps: string[];
  meetingLink: string | null;
  redirectionLinkForPurchase: string | null;
  description: string;
  firmServiceDetails: string[];
  isActive: boolean;
  vendorDetails: {
    id: string;
    name: string;
    description: string;
  };
  product: {
    id: string;
    title: string;
    description: string;
    category: MarketplaceServiceType;
    providedByTypes: MarketplaceServiceProviderType[];
    billingInterval: MarketplaceProductBillingInterval;
    pricingVariants: MarketplaceProductPricingVariant[];
  };
  vendorPricingVariants: {
    id: string;
    totalPrice: bigint | null;
    sla: {
      maxDay: string;
      minDay: string;
    };
    productPricingVariant: {
      id: string;
      country: Country;
      price: bigint | null;
      currency: string;
      priceDisplaySuffix: string;
    };
  }[];
};

interface ISLA {
  maxDay: string;
  minDay: string;
}

export type TMarketplaceVendorServiceByPricingVariant = {
  id: string; // marketplaceProductVendorPricingVariantId
  totalPrice: number;
  sla: ISLA | null;
  discountCode: string | null;
  priceDisplaySuffix: string | null;
  productByVendor: {
    id: string; // MarketplaceProductByVendorId
    steps: string[];
    meetingLink: string | null;
    redirectionLinkForPurchase: string | null;
    description: string;
    firmServiceDetails: string[];
  };
  vendorDetails: {
    id: string; // CAFirmId
    name: string;
    description: string;
  };
  product: {
    id: string;
    title: string;
    description: string;
    category: MarketplaceServiceType;
    providedByTypes: MarketplaceServiceProviderType[];
    billingInterval: MarketplaceProductBillingInterval;
    productPricingVariant: {
      id: string; // MarketplaceProductPricingVariantId
      country: Country;
      currency: string;
    };
  };
};

export type TMarketplaceService = {
  id: string;
  caFirm: {
    id: string;
    name: string;
    description: string;
    supportedCountries: Country[];
    defaultAccountantCalComId: string;
    defaultAccountantId: string;
  };
  company: {
    id: number;
    displayName: string;
  };
  corporation: {
    id: number;
    displayName: string;
    formationData: string;
  };
  status: MarketplaceServiceStatus;
  syntheticStatus: GenericStatus;
  chatRoomId?: string;
  statusUpdates: TStatusUpdate[];
  files: IFile[];
  serviceDetails: {
    id: string;
    name: string;
    country: Country;
  };
};
